import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  Typography,
  message,
  Row,
  Col,
  Divider,
  Alert
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { 
  ActionButton,
  TabContentWrapper,
  AnimatedFormSection,
  AnimatedPreviewPanel
} from './NewStyled';
import SettingsService from '../../services/SettingsService';

const { TextArea } = Input;

const AppointmentSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [bookingStats, setBookingStats] = useState(null);
  const [settings, setSettings] = useState({
    welcomeTitle: 'Schedule Your Appointment',
    welcomeMessage: "Welcome to the Black Heart Association's scheduling page! Our mobile units are here to serve your community with free heart health screenings and life-saving education. Select the service you'd like to book below.",
    organizationName: 'Black Heart Association',
    contactEmail: 'Tara@blackheartassociation.org',
    contactPhone: '972-646-0031',
    maxBookingsPerMonth: null,
    maxMembersPerYear: null
  });

  useEffect(() => {
    // Load settings from backend
    loadSettings();
    loadBookingStats();
  }, []);

  const loadBookingStats = async () => {
    try {
      const response = await SettingsService.getBookingLimits();
      if (response.success) {
        setBookingStats(response.data);
      }
    } catch (error) {
      console.error('Error loading booking stats:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await SettingsService.getAppointmentSettings();
      if (response.success) {
        setSettings(response.data);
        form.setFieldsValue(response.data);
      } else {
        message.error(response.error || 'Failed to load settings');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load settings');
    }
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);
      
      const response = await SettingsService.updateAppointmentSettings(values);
      if (response.success) {
        message.success('Settings saved successfully');
        setSettings(values);
        // Reload booking stats to reflect any changes
        loadBookingStats();
      } else {
        message.error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(settings);
    message.info('Form reset to saved values');
  };

  return (
    <TabContentWrapper>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <AnimatedFormSection>
            <Card title="Public Booking Page Settings" style={{ marginBottom: '24px' }}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={settings}
            >
              <Form.Item
                name="welcomeTitle"
                label="Welcome Title"
                rules={[{ required: true, message: 'Please enter a welcome title' }]}
              >
                <Input 
                  placeholder="e.g., Schedule Your Appointment"
                />
              </Form.Item>

              <Form.Item
                name="welcomeMessage"
                label="Welcome Message"
                rules={[{ required: true, message: 'Please enter a welcome message' }]}
              >
                <TextArea
                  rows={6}
                  placeholder="Welcome message displayed to users on the booking page"
                  style={{ minHeight: '120px', fontSize: '16px', lineHeight: '1.6' }}
                />
              </Form.Item>

              <Form.Item
                name="organizationName"
                label="Organization Name"
                rules={[{ required: true, message: 'Please enter organization name' }]}
              >
                <Input 
                  placeholder="e.g., Black Heart Association"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="contactEmail"
                    label="Contact Email"
                    rules={[
                      { required: true, message: 'Please enter contact email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input 
                      placeholder="contact@organization.com"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="contactPhone"
                    label="Contact Phone"
                    rules={[{ required: true, message: 'Please enter contact phone' }]}
                  >
                    <Input 
                      placeholder="972-646-0031"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <div style={{ marginBottom: '16px' }}>
                <Typography.Title level={5} style={{ margin: '0 0 8px 0', color: '#1a1a1a' }}>
                  Booking Limits
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                  Set limits to control the number of bookings. When limits are reached, all event types will be disabled on the public booking page. Leave empty for no limits.
                </Typography.Text>
              </div>

              {bookingStats && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                  message="Current Statistics"
                  description={
                    <div>
                      <div>Monthly Bookings: {bookingStats.stats?.monthly?.current || 0} 
                        {bookingStats.stats?.monthly?.limit && ` / ${bookingStats.stats.monthly.limit}`}
                        {bookingStats.stats?.monthly?.limitReached && <span style={{ color: 'red' }}> (Limit Reached)</span>}
                      </div>
                      <div>Yearly Members: {bookingStats.stats?.yearly?.current || 0}
                        {bookingStats.stats?.yearly?.limit && ` / ${bookingStats.stats.yearly.limit}`}
                        {bookingStats.stats?.yearly?.limitReached && <span style={{ color: 'red' }}> (Limit Reached)</span>}
                      </div>
                    </div>
                  }
                />
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="maxBookingsPerMonth"
                    label="Max Bookings Per Month"
                    tooltip="Maximum number of bookings allowed per calendar month. Leave empty for no limit."
                  >
                    <InputNumber 
                      placeholder="e.g., 100"
                      min={1}
                      max={10000}
                      style={{ 
                        width: '100%',
                        textAlign: 'center'
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="maxMembersPerYear"
                    label="Max Members Per Year"
                    tooltip="Maximum number of unique members (by email) allowed per calendar year. Leave empty for no limit."
                  >
                    <InputNumber 
                      placeholder="e.g., 500"
                      min={1}
                      max={50000}
                      style={{ 
                        width: '100%',
                        textAlign: 'center'
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <ActionButton 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    icon={<FontAwesomeIcon icon={faSave} />}
                  >
                    Save Settings
                  </ActionButton>
                  <Button onClick={handleReset}>
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
          </AnimatedFormSection>
        </Col>

        <Col xs={24} lg={8}>
          <AnimatedPreviewPanel>
            <Card title="Preview" size="small">
              <div style={{ 
                padding: '16px', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', color: '#1a1a1a' }}>
                  {form.getFieldValue('welcomeTitle') || settings.welcomeTitle}
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                  {form.getFieldValue('welcomeMessage') || settings.welcomeMessage}
                </p>
                <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>
                  <div><strong>Organization:</strong> {form.getFieldValue('organizationName') || settings.organizationName}</div>
                  <div><strong>Email:</strong> {form.getFieldValue('contactEmail') || settings.contactEmail}</div>
                  <div><strong>Phone:</strong> {form.getFieldValue('contactPhone') || settings.contactPhone}</div>
                </div>
              </div>
            </Card>
          </AnimatedPreviewPanel>
        </Col>
      </Row>
    </TabContentWrapper>
  );
};

export default AppointmentSettings;
